import './index.scss';
import 'jspolyfill-array.prototype.findIndex';
import 'array-flat-polyfill';

import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";


// Bundled pages
import Welcome from './pages/Welcome';
import Selections from './pages/Selections';
import SelectionsMarine from './pages/Selections/SelectionsMarine';
import PreDesigns from './pages/PreDesigns';
import ContactKone from './pages/ContactKone/ContactKone';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import DesignShare from './components/DesignShare'
import LoadingSpinner from './components/LoadingSpinner';
import { NEW_BUILDINGS, EXISTING_BUILDINGS } from './constants';
import MobileDownloadPage from './pages/MobileDownloadPage';
import GendocShareLoadingView from './components/GendocShareLoadingView/GendocShareLoadingView';
import DesignSpecification from './pages/DesignSpecification'
import Loader from './components/Loader';
import { AuthContext } from './store/auth';
import { DataContext } from './store/data';
import { DesignContext } from './store/design';
import { attemptAsync } from './utils/async-utils';

// Lazy pages
const Editor = lazyWithRetry(() => import('./pages/Editor'), 'Editor')
const ImageRendererPage = lazyWithRetry(() => import('./pages/ImageRendererPage'), 'ImageRendererPage')
const GeneratorPage = lazyWithRetry(() => import('./pages/GeneratorPage'), 'GeneratorPage')
const Provider2 = lazyWithRetry(() => import('./store/index2'), 'index2');

// buildingsType has to match one of these values (or can be omitted)
const buildingsTypeParam = `:buildingsType(${NEW_BUILDINGS}|${EXISTING_BUILDINGS})?`

function RoutesComponent() {
  return (
    <Routes>
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/download" element={<MobileDownloadPage />} />
      <Route path="/marine/error" element={<ErrorPage marine={true} />} />
      <Route path="/contact" element={<ContactKone />} />
      <Route path="/marine/share/:designId" element={<DesignShare isMarine={true} />} />
      <Route path="/share/:designId" element={<DesignShare />} />
      <Route path="/doc/:designId" element={<DesignShare gendoc={true} />} />

      <Route path="/" element={<Welcome />} />
      <Route path="/marine" element={<Welcome marine={true} />} />
      <Route path="/marine/selections" element={<ProtectedRoute element={<SelectionsMarine />} />} />
      <Route path={`/${buildingsTypeParam}/selections`} element={<ProtectedRoute element={<Selections />} />} />
      <Route path={`/${buildingsTypeParam}/selections/global`} element={<ProtectedRoute element={<Selections />} />} />
      <Route path={`/${buildingsTypeParam}/:country/:product/:releaseId?/predesigns`} element={<ProtectedRoute element={<PreDesigns />} />} />
      <Route path={`/${buildingsTypeParam}/:country/:product/:releaseId?/specification/:designId`} element={<DesignSpecification />} />

      <Route path="/doc/:designId/:mode" element={<EditorRoute />} />
      <Route path={`/${buildingsTypeParam}/:country/:product/:releaseId?/edit/blank`} element={<EditorRoute />} />
      <Route path={`/${buildingsTypeParam}/:country/:product/:releaseId?/edit/:designId`} element={<EditorRoute />} />
      <Route path="/generator/:designId" element={<Suspense fallback={<SuspenseFallback />}><Provider2><GeneratorPage /></Provider2></Suspense>} />
      <Route path={`/${buildingsTypeParam}/:country/:product/:releaseId?/specification/:designId/render`} element={<Suspense fallback={<SuspenseFallback />}><Provider2><ImageRendererPage /></Provider2></Suspense>} />

      <Route path="/marine/*" element={<Navigate to="/marine" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function SuspenseWrapper({ children }) {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Provider2>{children}</Provider2>
    </Suspense>
  )
}

function EditorRoute() {
  return (
    <SuspenseWrapper>
      <Editor />
    </SuspenseWrapper>
  )
}

function ProtectedRoute({ element }) {
  const params = useParams()
  const auth = useContext(AuthContext)
  const settings = getAuthRouteSettings({ params, accessibleWithDesign: false, accessibleWithEditableDesign: false })
  const isAccessible = useIsAccessible(settings)

  useEffect(() => {
    if (!isAccessible) {
      auth.signIn(true)
    }
  }, [auth, isAccessible])

  if (isAccessible) {
    return element
  }

  return null
}

function SuspenseFallback(props) {
  const location = useLocation()
  
  const progress = location.state?.progress || 0
  const isEditable = location.state?.isEditable

  if (location.pathname.includes('/doc/')) {
    // If refreshing the page on gendoc viewer page
    return (
      <GendocShareLoadingView progress={progress} isEditable={isEditable} />
    )
  }

  if (location.pathname.includes('/edit/')) {
    // Editor specific loader
    return <Loader progress={progress} />
  }

  // Default case
  return (
    <LoadingSpinner />
  )
}

/**
 * Checks if the route can be accessed based on the domain, design state, and auth state.
 * Only India domain has requireAuth=true. For any other domain, every route is accessible by anyone.
 * Any route can be accessed by a logged in user.
 * Some routes can be accessed with a valid design id (e.g. through a share link). This is specified with accessibleWithDesign option.
 * Other routes can be accessed only with a design that has been flagged as editableByPublic. This is specified with accessibleWithEditableDesign option.
 * accessibleWithEditableDesign is prioritized over accessibleWithDesign, i.e. if both are true, design must be flagged as editableByPublic. 
 * @param {Object} options 
 * @param {Object | boolean} options.accessibleWithDesign 
 * @param {Object | boolean} options.accessibleWithEditableDesign 
 * @returns 
 */
function useIsAccessible(options = {}) {
  const { domainCountry } = useContext(DataContext)
  const { loggedInUser } = useContext(AuthContext)
  const { design } = useContext(DesignContext)

  if (!domainCountry.requireAuth) return true
  if (loggedInUser) return true
  

  if (options.accessibleWithEditableDesign) {
    return !!design?.editableByPublic
  }

  if (options.accessibleWithDesign) {
    return !!design
  }

  return false
}

/**
 * Gets settings for specific route parameter value, or default settings if no parameter specific settings defined.
 */
function getAuthRouteSettings({ params, paramSettings, accessibleWithDesign, accessibleWithEditableDesign }) {
  let result

  if (paramSettings) {
    Object.entries(params).forEach(entry => {
      if (result) return
      const [ key, value ] = entry
      
      if (paramSettings[key]) {
        result = paramSettings[key][value]
      }
    })
  }

  if (!result) {
    return {
      accessibleWithDesign,
      accessibleWithEditableDesign
    }
  }

  return result
}


// Sometimes, rarely, React.lazy fails to load a JS chunk (that definitely exists) in production for some reason.
// Trying a few times before crashing.
function lazyWithRetry(componentImport, name) {
  return lazy(async () => {
    try {
      const component = await attemptAsync({
        fn: componentImport,
        retries: 3,
        delays: [1500, 3000, 4500]
      })
      
      return component
    } catch (err) {
      const item = localStorage.getItem('LAST_REFRESH')
      const now = Date.now()
      const oneMinute = 1000 * 60 * 1
      if (item && !isNaN(item)) {
        if (now - oneMinute < Number(item)) {
          // Only reload once in a minute so the app does not get stuck in a reload loop
          // if the loading never succeeds. Instead it should go to the error page.
          throw new Error(`Failed to load lazy component "${name}".`)
        }
      }

      // Store the reload time so we can check if we should reload or go to the error page next time.
      localStorage.setItem('LAST_REFRESH', String(now))
      window.location.reload()
    }
  })
}

export default RoutesComponent